import { Router } from '@angular/router';
import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { ApplicationInsights, IAppInsightsCore, IConfiguration, IProcessTelemetryContext, ITelemetryItem, ITelemetryPlugin } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { environment } from 'src/environments/environment';
import { generateW3CId, isValidSpanId, isValidTraceId } from '@microsoft/applicationinsights-core-js';
import { TelemetryContextService, TelemetrySnapshot } from './telemetry-context.service';

export interface RequestTelemetry {
  readonly snapshot: TelemetrySnapshot;
  readonly traceId: string;
  readonly parentId?: string;
  readonly spanId: string;
  readonly traceparent: string;
  readonly started: Date;
  readonly elapsedStart: number;
  readonly method: string;
  readonly url: string;
}

export type HttpDependencyOutcome =
  | { readonly type: 'http'; readonly status: number }
  | { readonly type: 'network-error' }
  | { readonly type: 'timeout' }
  | { readonly type: 'cancelled' };

const MANUAL_FETCH_CONTEXT = Symbol('FlexManualDependency');
type ManualFetchOptions = RequestInit & { readonly [MANUAL_FETCH_CONTEXT]: RequestTelemetry };

function isManualFetchOptions(options: RequestInit): options is ManualFetchOptions {
  return MANUAL_FETCH_CONTEXT in options;
}

const APPROVED_API_URLS = [
  environment.legacyApiUrl, environment.authApiUrl, environment.activityApiUrl,
  environment.auditApiUrl, environment.projectApiUrl, environment.userApiUrl,
  environment.lookupApiUrl, environment.timeRecordApiUrl, environment.timeApiUrl,
  environment.mobileApiUrl, environment.attachmentApiUrl, environment.rolesApiUrl,
  environment.flexFormsApiUrl, environment.notificationApiUrl, environment.webApiBaseUrl,
];
const STATIC_REQUEST = /\/(?:assets|svg)\/|\.(?:css|js)$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTelemetrySnapshot(value: unknown): value is TelemetrySnapshot {
  return isRecord(value) && isRecord(value.properties) && Object.values(value.properties).every(property => typeof property === 'string') &&
    ['version', 'operatingSystem', 'model'].every(key => value[key] === undefined || typeof value[key] === 'string');
}

/** SDK overrides extend Flex defaults; attribution, approved correlation and excluded pageviews remain Flex policy. */
type TelemetrySdkConfiguration = Partial<ConstructorParameters<typeof ApplicationInsights>[0]['config']>;
export const FLEX_TELEMETRY_CONFIG = new InjectionToken<TelemetrySdkConfiguration>('FlexTelemetryConfiguration');

/** Owns SDK collection, enrichment and request instrumentation; never executes application HTTP. */
@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService implements OnDestroy {
  private appInsights: ApplicationInsights;
  private customMetadataPlugin: CustomMetadataPlugin;
  private manualAttempt?: RequestTelemetry;
  private readonly completedRequests = new WeakSet<RequestTelemetry>();

  constructor(
    _router: Router,
    private context: TelemetryContextService,
    @Optional() @Inject(FLEX_TELEMETRY_CONFIG) private configuration?: TelemetrySdkConfiguration
  ) {
    this.collect(() => this.initializeAppInsights());
  }

  private initializeAppInsights() {
    this.customMetadataPlugin = new CustomMetadataPlugin(this.context);
    const angularPlugin = new AngularPlugin();
    const approvedUrls = APPROVED_API_URLS.flatMap(value => {
      try { return [new URL(value, location.origin)]; } catch { return []; }
    });
    const allowedCorrelation = approvedUrls.map(url => {
      const base = (url.origin + url.pathname.replace(/\/+$/, '')).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return `${base}(?:/|[?#]|$)`;
    }).join('|');

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: environment.applicationInsightsConnectionString,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,
        disableFetchTracking: true,
        disableAjaxTracking: false,
        isBeaconApiDisabled: false,   // allow Beacon for background/low-overhead sending
        maxBatchInterval: 20000,      // batch telemetry for up to 20s
        maxBatchSizeInBytes: 10000,   // small batches for constrained devices
        enableUnhandledPromiseRejectionTracking: true, // capture async errors
        isRetryDisabled: false,       // retry failed sends when network returns
        bufferOverride: {
          getItem: (logger, key) => { try { return localStorage.getItem(key); } catch { return null; } },
          setItem: (logger, key, value) => {
            try { localStorage.setItem(key, value); return true; } catch { return false; }
          },
        },
        ...this.configuration,
        enableAutoRouteTracking: false,
        excludeRequestFromAutoTrackingPatterns: [
          new RegExp(`^(?!(?:${allowedCorrelation}))(?:[^?#]*/(?:assets|svg)/|[^?#]*\\.(?:css|js)(?:[?#]|$))`, 'i'),
          ...(this.configuration?.excludeRequestFromAutoTrackingPatterns || []).map(pattern => typeof pattern === 'string' ? new RegExp(pattern) : pattern),
        ],
        extensions: [...(this.configuration?.extensions || []), this.customMetadataPlugin, angularPlugin],
        extensionConfig: {
          ...this.configuration?.extensionConfig,
          [angularPlugin.identifier]: { ...this.configuration?.extensionConfig?.[angularPlugin.identifier], router: undefined },
        },
        enableCorsCorrelation: true,
        correlationHeaderDomains: approvedUrls.map(url => url.host),
        correlationHeaderExcludePatterns: [new RegExp(`^(?!(?:${allowedCorrelation}))`, 'i'), ...(this.configuration?.correlationHeaderExcludePatterns || [])],
      },
    });

    this.appInsights.addTelemetryInitializer(event => {
      if (event.baseType?.includes('Pageview')) {
        return false;
      }

      return true;
    });

    this.appInsights.loadAppInsights();
    this.appInsights.addDependencyListener(details => {
      const fetchAttempt = details.init && isManualFetchOptions(details.init) ? details.init[MANUAL_FETCH_CONTEXT] : undefined;
      const manual = details.xhr ? this.manualAttempt : fetchAttempt;
      details.context = { ...details.context, flexManual: !!manual, flexSnapshot: manual?.snapshot || this.captureSnapshot() };
      if (manual) {
        details.traceId = manual.traceId;
        details.spanId = manual.spanId;
        details.traceFlags = 1;
      }
    });
    this.appInsights.addDependencyInitializer(details => {
      if (details.context?.flexManual) { return false; }
      if (details.context?.flexSnapshot) {
        details.item.properties = this.properties(details.item.properties, details.context.flexSnapshot);
      }
      return true;
    });
  }

  ngOnDestroy(): void {
    this.collect(() => this.appInsights?.unload(false));
  }

  logPageView(name?: string, url?: string) {
    this.collect(() => this.appInsights?.trackPageView({
      name: name,
      uri: url,
    }));
  }

  logEvent(name: string, properties?: { [key: string]: any }, snapshot?: TelemetrySnapshot, operation?: Pick<RequestTelemetry, 'traceId' | 'parentId'>) {
    this.collect(() => this.appInsights?.trackEvent({ name, properties: this.properties(properties, snapshot, operation) }));
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.collect(() => this.appInsights?.trackMetric({ name, average }, this.properties(properties)));
  }

  logException(exception: Error, properties?: { [key: string]: any }, severityLevel?: number) {
    this.collect(() => this.appInsights?.trackException({ exception, severityLevel, properties: this.properties(properties) }));
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.collect(() => this.appInsights?.trackTrace({ message }, this.properties(properties)));
  }

  flush() {
    this.collect(() => this.appInsights?.flush());
  }

  startDiagnosticsSession(id: string, expiry = Date.now() + 3600000) {
    this.context.publishDiagnostic(id, expiry);
  }

  stopDiagnosticsSession() {
    this.context.publishDiagnostic();
  }

  /**
   * Exposes the current telemetry trace context (traceId & parentId/spanId) for correlation headers.
   */
  public getTelemetryTrace() {
    return this.appInsights?.context?.telemetryTrace;
  }

  public generateSpanId(): string {
    return generateW3CId().substring(0, 16);
  }

  private collect(action: () => void): void {
    try { action(); } catch {}
  }

  private captureSnapshot(): TelemetrySnapshot {
    try { return this.context.capture(); }
    catch { return Object.freeze({ properties: Object.freeze({ MetadataUnavailable: '{"TelemetryContext":"failed"}' }) }); }
  }

  private properties(properties?: Record<string, unknown>, snapshot = this.captureSnapshot(), operation?: Pick<RequestTelemetry, 'traceId' | 'parentId'>): Record<string, unknown> {
    return {
      ...properties,
      __flexContext: JSON.stringify(snapshot),
      ...(operation ? { __flexTrace: JSON.stringify({ traceID: operation.traceId, parentID: operation.parentId || null }) } : {})
    };
  }

  isServiceUrl(url: string): boolean {
    try {
      const target = new URL(url, location.origin);
      if (target.username || target.password) { return false; }
      return APPROVED_API_URLS.some(value => {
        const service = new URL(value, location.origin);
        const path = service.pathname.replace(/\/+$/, '');
        return target.origin === service.origin && (target.pathname === path || target.pathname.startsWith(path + '/'));
      });
    } catch { return false; }
  }

  isStaticRequest(url: string): boolean {
    try { return !this.isServiceUrl(url) && STATIC_REQUEST.test(new URL(url, location.origin).pathname); }
    catch { return false; }
  }

  beginRequest(
    method: string,
    url: string,
    snapshot = this.captureSnapshot(),
    operation?: Pick<RequestTelemetry, 'traceId' | 'parentId'>
  ): RequestTelemetry {
    const candidateTrace = operation?.traceId || this.getTelemetryTrace()?.traceID;
    const traceId = isValidTraceId(candidateTrace) ? candidateTrace : generateW3CId();
    const spanId = this.generateSpanId();
    const candidateParent = operation ? operation.parentId : this.getTelemetryTrace()?.parentID;
    const parentId = isValidSpanId(candidateParent) ? candidateParent : undefined;
    return Object.freeze({
      snapshot,
      traceId,
      parentId,
      spanId,
      traceparent: `00-${traceId}-${spanId}-01`,
      started: new Date(),
      elapsedStart: performance.now(),
      method,
      url
    });
  }

  /** Angular XHR starts synchronously on subscription; the SDK copies this context into that request's record. */
  instrumentRequest<T>(attempt: RequestTelemetry, subscribe: () => T): T {
    const previous = this.manualAttempt;
    this.manualAttempt = attempt;
    try { return subscribe(); }
    finally { this.manualAttempt = previous; }
  }

  /** Marks only this browser fetch initiation; does not execute, abort, or exclude its URL from later tracking. */
  manualFetchOptions(attempt: RequestTelemetry, options: RequestInit = {}): RequestInit {
    const initiation: ManualFetchOptions = { ...options, [MANUAL_FETCH_CONTEXT]: attempt };
    return initiation;
  }

  correlationHeaders(url: string, attempt: RequestTelemetry): Record<string, string> {
    return this.isServiceUrl(url) ? { traceparent: attempt.traceparent } : {};
  }

  /** Records an attempt at most once. Telemetry failures never override its application outcome. */
  completeRequest(attempt: RequestTelemetry, outcome: HttpDependencyOutcome): void {
    if (this.completedRequests.has(attempt)) { return; }
    this.completedRequests.add(attempt);
    try {
      const target = new URL(attempt.url, location.origin);
      const status = outcome.type === 'http' ? outcome.status : 0;
      this.appInsights?.trackDependencyData({
        id: `|${attempt.traceId}.${attempt.spanId}.`,
        name: `${attempt.method} ${target.pathname}`,
        target: target.host,
        data: target.origin + target.pathname,
        type: 'HTTP',
        startTime: attempt.started,
        duration: Math.max(0, performance.now() - attempt.elapsedStart),
        success: outcome.type === 'http' && status >= 200 && status < 400,
        responseCode: status,
        properties: this.properties({
          Outcome: outcome.type,
          Cancelled: String(outcome.type === 'cancelled'),
          ...(outcome.type === 'cancelled' ? { CancellationScope: 'client-wait' } : {}),
          AttemptId: attempt.spanId,
          TraceId: attempt.traceId
        }, attempt.snapshot, attempt),
      });
    } catch {}
  }

}

export class CustomMetadataPlugin implements ITelemetryPlugin {
  identifier: string = 'CustomMetadataPlugin';
  priority: number = 300;
  private nextPlugin?: ITelemetryPlugin;
  constructor(private context: TelemetryContextService) {}

  public initialize(config: IConfiguration, core: IAppInsightsCore, extensions: ITelemetryPlugin[]): void { }

  public processTelemetry(env: ITelemetryItem, itemCtx?: IProcessTelemetryContext): void {
    const props = env.baseData?.properties || {};
    const data = env.data || {};
    const serialized = props.__flexContext || data.__flexContext;
    const trace = props.__flexTrace || data.__flexTrace;
    for (const key of ['__flexContext', '__flexTrace']) {
      delete props[key];
      delete data[key];
    }
    try {
      const snapshot: unknown = serialized ? JSON.parse(serialized) : this.context.capture();
      if (isTelemetrySnapshot(snapshot)) {
        for (const key of ['InstallationId', 'AppRunId', 'LoginContextId', 'UserId', 'InputType', 'AppBuild', 'Platform', 'OSVersion', 'DebugSessionId', 'MetadataUnavailable']) {
          delete props[key];
          delete data[key];
        }
        env.baseData = env.baseData || {};
        env.baseData.properties = { ...props, ...snapshot.properties };
        env.ext = env.ext || {};
        env.ext.user = { ...env.ext.user, authId: snapshot.properties.UserId };
        env.tags = { ...env.tags, 'ai.cloud.role': 'Flex', 'ai.application.ver': snapshot.version, 'ai.user.authUserId': snapshot.properties.UserId };
        if (snapshot.operatingSystem) { env.ext.os = { ...env.ext.os, name: snapshot.operatingSystem }; }
        if (snapshot.model) { env.ext.device = { ...env.ext.device, model: snapshot.model }; }
      }
    } catch {}
    try {
      const operation: unknown = trace ? JSON.parse(trace) : undefined;
      if (isRecord(operation) && typeof operation.traceID === 'string' && isValidTraceId(operation.traceID) &&
          (operation.parentID === null || (typeof operation.parentID === 'string' && isValidSpanId(operation.parentID)))) {
        env.ext = env.ext || {};
        env.ext.trace = { ...env.ext.trace, traceID: operation.traceID, parentID: operation.parentID };
      }
    } catch {}
    if (itemCtx) { itemCtx.processNext(env); }
    else { this.nextPlugin?.processTelemetry(env); }
  }

  public setNextPlugin(next: ITelemetryPlugin): void {
    this.nextPlugin = next;
  }

}
